import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";

/**
 * The Extended PoP that delivers a port (CTX-802). For the customer the port
 * sits on that site — the equipment they have on premises — not on the core
 * POP the site uplinks to. The backend only sends a site the viewer can see.
 */
export interface PortExtenderPop {
  id: number;
  /** Service ID of the CPE (80-XXXXXX). */
  serviceId: string;
  name: string | null;
}

interface PortLocationSource {
  extenderPop: PortExtenderPop | null;
  datacenter?: { name?: string | null } | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shapePortExtenderPop = (raw: any): PortExtenderPop | null =>
  raw && Number.isInteger(raw.id) && raw.serviceId
    ? { id: raw.id, serviceId: raw.serviceId, name: raw.name ?? null }
    : null;

/** What the POP column and the Location row show: the site's id, else the POP. */
export const portLocationLabel = (port: PortLocationSource): string =>
  port.extenderPop?.serviceId || port.datacenter?.name || "-";

/** A site without its city: "KIO QRO1 (Queretaro, MX)" → "KIO QRO1", for one-line summaries. */
export const shortSiteName = (name: string | null | undefined): string =>
  (name ?? "").replace(/\s*\([^)]*\)\s*$/, "");

/**
 * What the POP filter matches: the site by its id and name, or the POP.
 * A port on a site does not match its core POP — the filter finds what the
 * column shows.
 */
export const portLocationSearchText = (port: PortLocationSource): string =>
  port.extenderPop
    ? [port.extenderPop.serviceId, port.extenderPop.name ?? "", "CPE", "Extended PoP"]
        .filter(Boolean)
        .join(" ")
    : (port.datacenter?.name ?? "");

export const extenderPopDetailPath = (extenderPop: PortExtenderPop): string =>
  buildDetailPath("extenderPop", { extenderPopId: extenderPop.id });
